import React from 'react'
import {UIGameActionButton} from '../../../common/UIGameActionButton'
import {TradePlayerResponseStatus, TradeValidator} from '@colonist/game-mechanic-validators'
import {TradeResponseStatus} from '@colonist/game-states'
import {TranslationObject} from '@colonist/i18n'
import styles from '../UIGameTradeOffers.module.scss'
import clsx from 'clsx'
import {T} from '@colonist/ui'
import {ImageURL} from '@colonist/types'
import IconCheck from '@static/images/icon_check.svg'
import IconHourglass from '@static/images/icon_hourglass.svg'
import IconX from '@static/images/icon_x.svg'
import IconTrade from '@static/images/icon_trade.svg'
import {useGameStore} from '../../../store/GameStoreContext'
import {usePlayerValidators} from '../../../store/selectors/usePlayerValidators'

const getPlayerResponseButtonImage = (responseStatus: TradeResponseStatus): ImageURL => {
    switch(responseStatus) {
        case TradeResponseStatus.Accepted: return IconCheck
        case TradeResponseStatus.PendingResponse: return IconHourglass
        case TradeResponseStatus.Rejected: return IconX
        case TradeResponseStatus.Embargoed: return IconTrade
    }
}

const getTextTipForStatus = (playerResponse: TradePlayerResponseStatus, username: string): TranslationObject => {
    switch(playerResponse.responseStatus) {
        case TradeResponseStatus.Accepted: return {
            key: 'strings:game.tooltips.tradeOfferExecuteTrade',
            options: {
                playerUsername: username,
            },
        }
        case TradeResponseStatus.Rejected: return {
            key: 'strings:game.tooltips.tradeOfferPlayerRejected',
            options: {
                playerUsername: username,
            },
        }
        case TradeResponseStatus.PendingResponse: return {
            key: 'strings:game.tooltips.tradeOfferPlayerPending',
            options: {
                playerUsername: username,
            },
        }
        case TradeResponseStatus.Embargoed: return {
            key: 'strings:game.tooltips.tradeOfferPlayerEmbargoed',
            options: {
                playerUsername: username,
                context: 'self',
            }}
    }
}

export interface UIGameTradeOfferRespondTradeButtonProps {
    className?: string | undefined
    tradeValidator: TradeValidator
    playerResponse: TradePlayerResponseStatus
    disableTooltips?: boolean | undefined
}

export const UIGameTradeOfferRespondTradeButton = ({
    className,
    tradeValidator,
    playerResponse,
    disableTooltips,
}: UIGameTradeOfferRespondTradeButtonProps) => {
    const onClickExecuteTrade = useGameStore((state) => state.gameTradeOffers.onClickExecuteTrade)
    const playerColor = playerResponse.player
    const {getPlayerValidator} = usePlayerValidators()
    const playerValidator = getPlayerValidator(playerColor)
    const myColor = useGameStore((state) => state.myColor)
    const tooltipText: TranslationObject = getTextTipForStatus(playerResponse, playerValidator.username)
    const executeTradeAction = tradeValidator.getExecuteTradeActionStatus(myColor)
    const isEnabled = executeTradeAction.canTakeAction ? playerResponse.responseStatus === TradeResponseStatus.Accepted : false
    const extraHelperText = !isEnabled ? tooltipText : undefined
    const helperText = executeTradeAction.errorMessageIfCantTakeAction || extraHelperText
    const iconPath = getPlayerResponseButtonImage(playerResponse.responseStatus)
    const bgColor = playerResponse.player
    const isPlayerEmbargoed = playerResponse.responseStatus === TradeResponseStatus.Embargoed
    const playerCreatingCounterOfferStatus = tradeValidator.getPlayersCreatingCounterOffer()
        .find((playerCreatingCounterOffer) => playerCreatingCounterOffer.player == playerResponse.player)
    const isCreatingCounterOffer = playerCreatingCounterOfferStatus?.isCreatingCounterOffer

    const handleClick = () => {
        onClickExecuteTrade(tradeValidator.state.id, playerResponse.player)
    }

    return (
        <UIGameActionButton
            tooltipContent={<T id={tooltipText}/>}
            tooltipDisabled={disableTooltips}
            className={clsx(styles.tradeButton, className)}
            backgroundColor={bgColor}
            iconPath={iconPath}
            onClick={handleClick}
            buttonType="trade"
            tooltipPosition='bottom'
            centerTextTooltip
            helperText={helperText}
            disabled={!isEnabled}
            isEmbargoed={isPlayerEmbargoed}
            isCreatingCounterOffer={isCreatingCounterOffer}
        />
    )
}
